import React, { useRef, useState } from 'react'
import {
	Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, FormControl, Typography,
	IconButton
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import stylesheet from '../detail-container.stylesheet'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'

interface IProps {
	openUpload: any
	input: any
	onUploadClose: any
	classes: ClassNameMap
}

const AddAppointmentsComponent: React.FC<IProps> = ({ openUpload, input, onUploadClose, classes }) => {
	const [bookingFile, setBookingFile] = useState(null as any)
	const [patientListFile, setPatientListFile] = useState(null as any)
	const patientService = new PatientService()
	const [fileLoading, setFileLoading] = useState(false)
	const addAppoinemenRef = useRef(null) as any

	const checkDisabledBtn = () => !bookingFile?.name && !patientListFile?.name
	const uploadPatientListFile = (e: any) => setPatientListFile(e.target.files[0])
	const uploadBookingFile = (e: any) => setBookingFile(e.target.files[0])

	// The dialog is reused across Optum and Healthy Heart. The hospital tag comes
	// in via `input.hospital` and is forwarded to the backend so appointments are
	// only created for patients that belong to the selected hospital.
	// HIPAA-critical: a missing `input.hospital` falls back to 'optum' for
	// backwards compatibility, but we log a console.warn so a future caller
	// that forgets the prop is visible during dev.
	if (input && !input.hospital) {
		console.warn('[AddAppointmentsComponent] input.hospital missing; defaulting to optum. This will misroute HealthyHeart appointments if the caller meant healthyheart.')
	}
	const hospitalTag: string = (input && input.hospital) || 'optum'

	const handleSubmit = () => {
		setFileLoading(true)
		const formData = new FormData();
		formData.append(`bookingFile`, bookingFile)
		formData.append(`elationReportFile`, patientListFile)

		patientService.addAppointmentsFiles(formData, hospitalTag).then((res: any) => {
			setFileLoading(false);
			(res.data.status === 'completed')
				? toast.success(res.data.message || `Appointments added successfully.`, Helper.bottom_center())
				: toast.error(res.data.message || `Failed to add appointment.`, {
					...Helper.bottom_center(),
					style: {
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
					}
				})
			onUploadClose(true)
		}).catch((err: any) => {
			toast.error(Helper.getErrorText(err), {
				...Helper.bottom_center(),
				style: {
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
				}
			})
			setFileLoading(false)
			onUploadClose(true)
		})
	}

	const handleFileDetails = (fileName: any, type: any) => {
		return <Grid container>
			<Grid item xs={6}>
				<Typography pt={0.5} variant='body2' className={classes.desc}>{fileName}</Typography>
			</Grid>
			<Grid item xs={5}>
				<IconButton size="small" disabled={fileLoading}
					onClick={() => type === 'patientList' ? setPatientListFile(null) : setBookingFile(null)}>
					<CloseIcon className={classes.editIcon} />
				</IconButton>
			</Grid>
		</Grid>
	}
	return (
		<Draggable ref={addAppoinemenRef} handle='.add-appointmenmts-dialog'>
			<Dialog
				fullWidth={true}
				maxWidth="sm"
				open={openUpload}
				onClose={() => onUploadClose(false)}
				aria-labelledby={'Add Appointmenmts'}
				hideBackdrop
				disableScrollLock
				onKeyUp={(e) => {
					e.preventDefault();
					if (e.key === 'Enter' && !checkDisabledBtn())
						handleSubmit()
				}}
				sx={{
					position: "absolute",
					left: '15%',
					top: '15%'
				}}
			>
				<DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'add-appointmenmts-dialog' }}>
					<DialogTitleHeaderComponent title={input.title} handleDialogClose={onUploadClose} />
				</DialogTitle>
				<Grid item>{fileLoading && <LinearProgress />}</Grid>
				<DialogContent className={classes.addPatientDialogContent}>
					<Grid container direction='column' spacing={2} className={classes.containerPadding}>
						<Grid item>
							<FormControl>
								<input
									accept={'.tsv'}
									style={{ display: 'none' }}
									id="upload-file"
									type="file"
									onChange={uploadBookingFile}
									disabled={fileLoading}
									multiple
								/>
								<label htmlFor="upload-file">
									<Button component="span" disabled={fileLoading}
										variant="outlined" size="small">Choose bookings file</Button>
								</label>
							</FormControl>
							<br />
							<Typography sx={{ fontSize: '0.75rem' }} component="span" color="grey">
								<i>{`Please select .tsv file.`}</i>
							</Typography>
						</Grid>

						<Grid item>
							<FormControl>
								<input
									accept={'.csv'}
									style={{ display: 'none' }}
									id="upload-patient-file"
									type="file"
									onChange={uploadPatientListFile}
									disabled={fileLoading}
									multiple
								/>
								<label htmlFor="upload-patient-file">
									<Button component="span" disabled={fileLoading}
										variant="outlined" size="small">Choose elation patient list file</Button>
								</label>
							</FormControl>
							<br />
							<Typography sx={{ fontSize: '0.75rem', paddingBottom: '1rem' }} component="span" color="grey">
								<i>{`Please select .csv file.`}</i>
							</Typography>
						</Grid>

						<Grid item xs>
							{bookingFile?.name && handleFileDetails(bookingFile?.name, 'bookingFile')}
							{patientListFile?.name && handleFileDetails(patientListFile?.name, 'patientList')}
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="outlined"
								size="small"
								disabled={fileLoading || checkDisabledBtn()}
								onClick={() => handleSubmit()}
								component="label"
							>
								Submit
							</Button>
						</Grid>
						<Grid item>
							<Button variant="outlined" size="small" onClick={onUploadClose}>
								Cancel
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
		</Draggable>
	)
}

export default withStyles(stylesheet)(AddAppointmentsComponent)